import type { RiskTier } from '../types/index.js';
import type { DriftFinding } from './types.js';

export function compareToolPolicy(
  expected: { allow: string[]; deny: string[] },
  actual: { allow?: string[]; deny?: string[] },
): DriftFinding[] {
  const findings: DriftFinding[] = [];
  const actualAllow = actual.allow ?? [];
  const actualDeny = actual.deny ?? [];

  // Tools in actual.allow not in expected.allow → critical
  for (const tool of actualAllow) {
    if (!expected.allow.includes(tool)) {
      findings.push({
        category: 'tool_policy',
        severity: 'critical',
        message: `Unauthorized tool added: "${tool}"`,
        expected: JSON.stringify(expected.allow),
        actual: JSON.stringify(actualAllow),
        field: 'tools.allow',
      });
    }
  }

  // Tools in expected.allow not in actual.allow → warning
  for (const tool of expected.allow) {
    if (!actualAllow.includes(tool)) {
      findings.push({
        category: 'tool_policy',
        severity: 'warning',
        message: `Declared tool removed: "${tool}"`,
        expected: JSON.stringify(expected.allow),
        actual: JSON.stringify(actualAllow),
        field: 'tools.allow',
      });
    }
  }

  // Tools in expected.deny not in actual.deny → critical
  for (const tool of expected.deny) {
    if (!actualDeny.includes(tool)) {
      findings.push({
        category: 'tool_policy',
        severity: 'critical',
        message: `Denied tool restriction removed: "${tool}"`,
        expected: JSON.stringify(expected.deny),
        actual: JSON.stringify(actualDeny),
        field: 'tools.deny',
      });
    }
  }

  return findings;
}

export function compareChannelConfig(
  expected: Record<string, unknown>,
  actual: Record<string, unknown>,
): DriftFinding[] {
  const findings: DriftFinding[] = [];

  // Channel enabled in actual but disabled in expected → critical
  for (const [channel, value] of Object.entries(actual)) {
    if (value === true && expected[channel] !== true) {
      findings.push({
        category: 'channel_config',
        severity: 'critical',
        message: `Unauthorized channel enabled: "${channel}"`,
        expected: String(expected[channel] ?? 'disabled'),
        actual: 'enabled',
        field: `channels.${channel}`,
      });
    }
  }

  // Channel disabled in actual but enabled in expected → warning
  for (const [channel, value] of Object.entries(expected)) {
    if (value === true && actual[channel] !== true) {
      findings.push({
        category: 'channel_config',
        severity: 'warning',
        message: `Declared channel disabled: "${channel}"`,
        expected: 'enabled',
        actual: String(actual[channel] ?? 'disabled'),
        field: `channels.${channel}`,
      });
    }
  }

  return findings;
}

const SANDBOX_STRENGTH: Record<string, number> = {
  all: 3,
  'non-main': 2,
  off: 1,
};

export function compareSandboxMode(
  _riskTier: RiskTier,
  expectedMode: string,
  actualMode: string,
): DriftFinding[] {
  const findings: DriftFinding[] = [];

  if (expectedMode === actualMode) {
    return findings;
  }

  const expectedStrength = SANDBOX_STRENGTH[expectedMode] ?? 0;
  const actualStrength = SANDBOX_STRENGTH[actualMode] ?? 0;

  if (actualStrength < expectedStrength) {
    findings.push({
      category: 'sandbox_weakening',
      severity: 'critical',
      message: `Sandbox weakened from "${expectedMode}" to "${actualMode}"`,
      expected: expectedMode,
      actual: actualMode,
      field: 'sandbox.mode',
    });
  } else {
    findings.push({
      category: 'sandbox_weakening',
      severity: 'warning',
      message: `Sandbox mode changed from "${expectedMode}" to "${actualMode}"`,
      expected: expectedMode,
      actual: actualMode,
      field: 'sandbox.mode',
    });
  }

  return findings;
}

export function compareFileHashes(
  expected: { charterHash: string; toolsHash: string },
  actual: { charterHash: string | null; toolsHash: string | null },
): DriftFinding[] {
  const findings: DriftFinding[] = [];

  // TOOLS.md hash
  if (actual.toolsHash === null) {
    findings.push({
      category: 'file_tampering',
      severity: 'warning',
      message: 'TOOLS.md not found on disk',
      expected: expected.toolsHash,
      actual: 'file not found',
      field: 'files.toolsHash',
    });
  } else if (actual.toolsHash !== expected.toolsHash) {
    findings.push({
      category: 'file_tampering',
      severity: 'critical',
      message: 'TOOLS.md modified outside Augmented',
      expected: expected.toolsHash,
      actual: actual.toolsHash,
      field: 'files.toolsHash',
    });
  }

  // CHARTER.md hash
  if (actual.charterHash === null) {
    findings.push({
      category: 'file_tampering',
      severity: 'warning',
      message: 'CHARTER.md not found on disk',
      expected: expected.charterHash,
      actual: 'file not found',
      field: 'files.charterHash',
    });
  } else if (actual.charterHash !== expected.charterHash) {
    findings.push({
      category: 'file_tampering',
      severity: 'warning',
      message: 'CHARTER.md modified outside Augmented',
      expected: expected.charterHash,
      actual: actual.charterHash,
      field: 'files.charterHash',
    });
  }

  return findings;
}
